<template>
  <div class="month-view h-full">
    <div class="month-header grid grid-cols-7 border-b">
      <div
        v-for="day in weekDays"
        :key="day"
        class="day-header-cell p-2 text-center font-medium"
      >
        {{ day }}
      </div>
    </div>

    <div class="month-body grid grid-cols-7 h-full">
      <div
        v-for="(day, index) in calendarDays"
        :key="index"
        class="day-cell border-b border-r p-1 min-h-[100px] relative"
        :class="{
          'bg-gray-50': day.month !== 'current',
          'bg-primary-50': isToday(day.date),
          'bg-red-50': isHoliday(day.date),
          'ring-2 ring-primary ring-inset': isSameDay(
            day.date,
            props.selectedDate,
          ),
          'cursor-not-allowed': isHoliday(day.date),
        }"
        @click="$emit('date-click', day.date)"
        @dblclick="$emit('dblclick-date', day.date)"
        @contextmenu.prevent="handleRightClick(day.date)"
      >
        <div
          class="day-number text-sm mb-1 flex justify-between items-center"
          :class="{
            'font-bold': day.month === 'current',
            'text-gray-400': day.month !== 'current',
          }"
        >
          <span
            class="rounded-full w-6 h-6 flex items-center justify-center"
            :class="{ 'bg-primary text-white': isToday(day.date) }"
          >
            {{ day.dayNumber }}
          </span>

          <button
            v-if="isHoliday(day.date)"
            class="text-xs text-red-500 hover:text-red-700"
            @click.stop="$emit('mark-holiday', day.date)"
          >
            <span class="sr-only">Holiday</span>
            <CalendarIcon class="h-4 w-4" />
          </button>
          <button
            v-else
            class="text-xs text-gray-400 hover:text-gray-600"
            @click.stop="$emit('mark-holiday', day.date)"
          >
            <span class="sr-only">Mark as Holiday</span>
            <CalendarIcon class="h-4 w-4" />
          </button>
        </div>

        <div v-if="isHoliday(day.date)" class="text-xs text-red-500 mb-1">
          {{ getHolidayName(day.date) }}
        </div>

        <div class="day-events space-y-1">
          <div
            v-for="(event, eventIndex) in getVisibleEventsForDay(day.date)"
            :key="event.id"
            class="event-item text-xs p-1 rounded truncate cursor-move group relative"
            :class="dashboardStore.getEventColorClass(event)"
            @click.stop="$emit('event-click', event)"
            draggable="true"
            @dragstart="handleDragStart($event, event)"
            @dragend="handleDragEnd"
          >
            <!-- Use slot if provided, otherwise use default rendering -->
            <slot
              name="event-tile"
              :event="event"
              :date="day.date"
              :view="'month'"
              :deletable="deletable"
              :delete-event="(id) => $emit('event-delete', id)"
            >
              <!-- Delete button -->
              <button
                v-if="deletable"
                class="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop="$emit('event-delete', event.id)"
              >
                <XMarkIcon class="h-3 w-3 text-red-500" />
              </button>

              <!-- Default event content -->
              <div class="flex flex-col">
                <div
                  v-if="eventTemplate.showTitle"
                  class="font-medium truncate"
                  :class="{
                    'line-clamp-1': eventTemplate.titleLines === 1,
                    'line-clamp-2': eventTemplate.titleLines === 2,
                  }"
                >
                  {{ event.title }}
                </div>
                <div
                  v-if="eventTemplate.showOrderNo"
                  class="font-medium truncate"
                  :class="{
                    'line-clamp-1': eventTemplate.titleLines === 1,
                    'line-clamp-2': eventTemplate.titleLines === 2,
                  }"
                >
                  {{ event.order_no }}
                </div>

                <div
                  v-if="eventTemplate.showTime && event.time"
                  class="text-xs opacity-75"
                >
                  {{ event.time }}
                </div>

                <div
                  v-if="eventTemplate.showType && event.type"
                  class="text-xs opacity-75"
                >
                  {{ event.type }}
                </div>

                <div
                  v-if="eventTemplate.showStatus && event.status"
                  class="text-xs opacity-75"
                >
                  {{ event.status }}
                </div>

                <div
                  v-if="eventTemplate.showNotes && event.notes"
                  class="text-xs opacity-75 mt-1"
                  :class="{
                    'line-clamp-1': eventTemplate.notesLines === 1,
                    'line-clamp-2': eventTemplate.notesLines === 2,
                  }"
                >
                  {{ event.notes }}
                </div>
              </div>
            </slot>
          </div>

          <button
            v-if="getEventsForDay(day.date).length > maxEventsPerDay"
            class="text-xs text-primary hover:text-primary-dark text-center w-full"
            @click.stop="handleShowMore(day.date, index)"
          >
            {{
              showMoreMap[index]
                ? "Show less"
                : `+${getEventsForDay(day.date).length - maxEventsPerDay} more`
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Events Modal -->
    <div
      v-if="showEventsModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    >
      <div class="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
        <button
          class="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          @click="showEventsModal = false"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
        <h2 class="text-lg font-semibold mb-4">
          Events for {{ modalDate ? modalDate.toLocaleDateString() : "" }}
        </h2>
        <ul class="space-y-2 max-h-96 overflow-y-auto">
          <li
            v-for="event in modalEvents"
            :key="event.id"
            class="p-3 rounded border bg-gray-50 cursor-pointer"
            :class="dashboardStore.getEventColorClass(event)"
            @click="$emit('event-click', event)"
          >
            <div class="font-medium">{{ event.title }}</div>
            <div v-if="event.time" class="text-xs text-gray-500">
              {{ event.time }}
            </div>
            <div v-if="event.type" class="text-xs text-gray-500">
              {{ event.type }}
            </div>
            <div v-if="event.status" class="text-xs text-gray-500">
              {{ event.status }}
            </div>
            <div v-if="event.notes" class="text-xs text-gray-500 mt-1">
              {{ event.notes }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDashboardStore } from "@/store/dashboard";
import { CalendarIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { showToast } from "@/composables/swal";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { computed, ref } from "vue";
import { OrderStatus } from "@/composables";
const dashboardStore = useDashboardStore();

interface Event {
  id: string;
  title: string;
  start: string;
  end?: string;
  time?: string;
  status: OrderStatus;
  type?: string;
  [key: string]: any;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  month: "prev" | "current" | "next";
}

// Define props
const props = defineProps({
  date: {
    type: Date,
    required: true,
  },
  selectedDate: {
    type: Date,
    required: true,
  },
  events: {
    type: Array as () => Event[],
    default: () => [],
  },
  holidays: {
    type: Array as () => Holiday[],
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: true,
  },
  deletable: {
    type: Boolean,
    default: true,
  },
  maxEventsPerDay: {
    type: Number,
    default: 5,
  },
  eventTemplate: {
    type: Object,
    default: () => ({
      showTitle: true,
      showTime: true,
      showType: false,
      showStatus: false,
      showNotes: false,
      titleLines: 1,
      notesLines: 0,
      showOrderNo: true,
    }),
  },
});

// Define emits
const emit = defineEmits([
  "date-click",
  "event-click",
  "event-drop",
  "event-delete",
  "mark-holiday",
  "switch-to-day-view",
  "dblclick-date",
]);

const draggedEvent = ref<Event | null>(null);
const showMoreMap = ref<Record<number, boolean>>({});
const showEventsModal = ref(false);
const modalDate = ref<Date | null>(null);

const modalEvents = computed(() => {
  if (!modalDate.value) return [];
  return getEventsForDay(modalDate.value);
});

const weekDays = computed(() => {
  return [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
});

const calendarDays = computed(() => {
  const days: CalendarDay[] = [];
  const monthStart = startOfMonth(props.date);
  const monthEnd = endOfMonth(props.date);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  let day = startDate;
  while (day <= endDate) {
    days.push({
      date: new Date(day),
      dayNumber: day.getDate(),
      month: isSameMonth(day, monthStart)
        ? "current"
        : day < monthStart
          ? "prev"
          : "next",
    });
    day = addDays(day, 1);
  }

  return days;
});

const isToday = (date: Date) => {
  return isSameDay(date, new Date());
};

const isHoliday = (date: Date) => {
  return props.holidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
};

const getHolidayName = (date: Date) => {
  const holiday = props.holidays.find((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });

  return holiday ? holiday.name : "";
};

const getEventsForDay = (date: Date) => {
  return props.events
    .filter((event) => {
      const eventDate = parseISO(event.start);
      return isSameDay(eventDate, date);
    })
    .map(
      (event) =>
        ({
          ...(event as Event),
          status: (event.status as OrderStatus) || OrderStatus.Pending,
        }) as Event,
    );
};

const getVisibleEventsForDay = (date: Date) => {
  const events = getEventsForDay(date);
  const dayIndex = calendarDays.value.findIndex((day) =>
    isSameDay(day.date, date),
  );

  if (showMoreMap.value[dayIndex]) {
    return events;
  }

  return events.slice(0, props.maxEventsPerDay);
};

const toggleShowMore = (dayIndex: number) => {
  showMoreMap.value = {
    ...showMoreMap.value,
    [dayIndex]: !showMoreMap.value[dayIndex],
  };
};

const handleDragStart = (e: DragEvent, event: Event) => {
  if (!props.editable || !e.dataTransfer) return;

  // Check if the event is on a holiday date
  const eventDate = parseISO(event.start);
  if (isHoliday(eventDate)) {
    e.preventDefault();
    showToast("Cannot move events from holiday dates", "warning");
    return;
  }

  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/plain", event.id);
  draggedEvent.value = event;
};

const handleDragEnd = (e: DragEvent) => {
  if (!props.editable || !draggedEvent.value || !e.dataTransfer) return;

  const dayCell = findClosestDayCell(e.clientX, e.clientY);
  if (dayCell && dayCell.dataset.date) {
    const newDate = new Date(dayCell.dataset.date);

    // Check if the target date is a holiday
    if (isHoliday(newDate)) {
      // Show warning message
      showToast("Cannot move events to holiday dates", "warning");
      return; // Don't emit the event-drop
    }

    emit("event-drop", draggedEvent.value.id, newDate);
  }

  draggedEvent.value = null;
};

const findClosestDayCell = (clientX: number, clientY: number) => {
  const dayCells = document.querySelectorAll(".day-cell");
  let closestCell = null;
  let closestDistance = Infinity;

  dayCells.forEach((cell, index) => {
    const rect = cell.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distance = Math.sqrt(
      Math.pow(centerX - clientX, 2) + Math.pow(centerY - clientY, 2),
    );

    if (distance < closestDistance) {
      closestDistance = distance;
      closestCell = cell;
      if (calendarDays.value[index]) {
        (closestCell as HTMLElement).dataset.date =
          calendarDays.value[index].date.toISOString();
      }
    }
  });

  return closestCell as HTMLElement | null;
};

const handleRightClick = (date: Date) => {
  emit("mark-holiday", date);
};

const handleShowMore = (date: Date, index: number) => {
  modalDate.value = date;
  showEventsModal.value = true;
};
</script>

<style scoped>
.bg-primary-50 {
  background-color: rgba(79, 70, 229, 0.05);
}

.day-cell {
  min-height: 100px;
}

.cursor-not-allowed {
  cursor: not-allowed !important;
}

.day-cell.cursor-not-allowed .event-item {
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .day-cell {
    min-height: 80px;
  }
}
</style>
